import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { AxiosResponse } from "axios";
import { TextField } from "@cmsgov/ds-medicare-gov";
import H1 from "../common/components/h1";
import Div from "../common/components/div";
import { SubmitButton } from "./buttons/Buttons";
import { MfaDeleteErrorResponse, MfaEnrollStep1Response, MfaEnrollStep2Response, SecurityCodeMethod, useMfaApi } from "./hooks/useMfaApi";
import { FlowName, SmartErrorAlert } from "../common/components/SmartErrorAlert";
import { hasErrorMessage } from "../common/functions/getApiErrorMessage";
import { validateOtp } from "./validators/mfaSchema";
import { useMfaPageView, useMfaInlineError } from "../common/hooks/use.analytics";
import { VerifyPassword } from "./VerifyPassword";
import { GetAnotherCode } from "./shared-components/GetAnotherCode";
import translate from "../common/functions/translate";
import capitalizeFirstLetter from "../common/functions/capitalizeFirstLetter";
import { MfaSendSuccessAlert } from "./shared-components/MfaSendSuccessAlert";
import BackNav from "../common/components/BackNav";
import { usePageTitle } from "../common/hooks/usePageTitle";
import getLocale from "../common/functions/getLocale";
import { mfaSetupSuccess } from "../common/analytics";
import useCheckForUpdateAccountCookie from "../common/hooks/useCheckForUpdateAccountCookie";
import useCheckForSession from "../common/hooks/useCheckForSession";
import { FullPageSpinner } from "../common/components/FullPageSpinner";
import { redirectTo } from "../common/functions/redirectTo";

interface ConfirmYourMethodProps {
    type: SecurityCodeMethod
    value: string
    isPasswordVerified?: boolean
    flow: 'enroll' | 'edit' | 'add'
    uuid?: string
}

export const ConfirmYourMethod = (props?: ConfirmYourMethodProps) => {
    const location = useLocation<ConfirmYourMethodProps>();
    const { type, value, flow, uuid } = location.state || props; //props comes in from LoginConfirmMethod;
    const [securityCode, setSecurityCode] = useState('');
    const [fieldErrorText, setFieldErrorText] = useState('');
    const [showFieldError, setShowFieldError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [step2EnrollResponse, setStep2EnrollResponse] = useState<AxiosResponse<MfaEnrollStep2Response> | null>(null);
    const [step1EnrollResponse, setStep1EnrollResponse] = useState<AxiosResponse<MfaEnrollStep1Response> | null>(null);
    const [showInfoAlert, setShowInfoAlert] = useState(true);
    const [isVerified, setIsVerified] = useState(true);
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [isCheckingCookie, setIsCheckingCookie] = useState(true);
    const [isResend, setIsResend] = useState(false);
    const [deleteMethodResponse, setDeleteMethodResponse] = useState<AxiosResponse<MfaDeleteErrorResponse> | null>(null);
    const confirmTitle = flow === 'edit' ? translate(`mfa_title_edit_${type}`) : translate(`mfa_title_${flow}`);
    const fieldLabel = translate('security_code');
    const inlineErr = useMfaInlineError(fieldLabel);

    useCheckForSession(setIsCheckingSession);
    useCheckForUpdateAccountCookie(setIsCheckingCookie, setIsVerified, isCheckingSession);

    const history = useHistory();
    const api = useMfaApi();
    usePageTitle(confirmTitle);
    useMfaPageView(flow, `${type} security code`);

    //Field validation
    useEffect(() => {
        const errorKeys = validateOtp(securityCode);
        setFieldErrorText(errorKeys ? translate(errorKeys[0]) : '');
    }, [securityCode]);

    function handle400Error(mfaEnrollStep2Response: AxiosResponse<MfaEnrollStep2Response> | null) {
        if (!mfaEnrollStep2Response) {
            return;
        }
        if (mfaEnrollStep2Response.data.errors) {
            //TODO MFA_TOO_MANY_ATTEMPTS WETGA-1450
            if (hasErrorMessage(mfaEnrollStep2Response?.data.errors, "MFA_EXPIRED_OTP")) {
                setFieldErrorText(translate('code_expired'));
                inlineErr.serverError(translate('code_expired'), 'MFA_EXPIRED_OTP');
            }
            if (hasErrorMessage(mfaEnrollStep2Response?.data.errors, "MFA_INVALID_OTP")) {
                setFieldErrorText(translate('invalid_code'));
                inlineErr.serverError(translate('invalid_code'), 'MFA_INVALID_OTP');
            }
        }
    }

    async function deleteMethod(oldMethodUuid: string) {
        setIsLoading(true);

        const removeMethodResponse = await api.mfaDelete(oldMethodUuid);
        setIsLoading(false);
        setDeleteMethodResponse(removeMethodResponse);

        if (removeMethodResponse?.data.code === 401) {
            setIsVerified(false);
        }
    }

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setShowFieldError(true);
        setIsVerified(true);

        if (fieldErrorText || isLoading) {
            if (fieldErrorText) {
                inlineErr.validationError(fieldErrorText);
            }
            return;
        }

        setIsLoading(true);
        const mfaEnrollStep2Response = await api.mfaEnrollStep2(securityCode);
        setIsLoading(false);
        setStep2EnrollResponse(mfaEnrollStep2Response);

        const code = mfaEnrollStep2Response?.data.code;
        if (code === 400) {
            handle400Error(mfaEnrollStep2Response);
            return;
        }

        if (code === 401) {
            setIsVerified(false);
            return;
        }

        if (code === 500 || code === 404) {
            return;
        }

        // analytics
        if (flow === 'add' || flow === 'enroll') {
            mfaSetupSuccess(type);
        }

        if (uuid) {
            await deleteMethod(uuid);
        }

        const id = mfaEnrollStep2Response?.data.configured_option && mfaEnrollStep2Response?.data.configured_option.id;
        const redirectUrl = `/my/account-settings?updated_mfa_method=${id}&lang=${getLocale()}`;
        redirectTo(redirectUrl, 'Successfully confirmed OTP.');
    }
    

    const isResendResponse = (step1EnrollResponse && !step2EnrollResponse);
    const smartErrorName: FlowName = `MfaEnrollStep${isResendResponse ? '1' : '2'}${capitalizeFirstLetter(type)}`;

    async function getAnotherCode() {
        setShowInfoAlert(false);
        const mfaEnrollStep1Response = await api.mfaEnrollStep1({
            type,
            value
        });

        setStep1EnrollResponse(mfaEnrollStep1Response);
        setStep2EnrollResponse(null);
        const code = mfaEnrollStep1Response?.data.code;
        if (code === 401) {
            setIsVerified(false);
            return;
        }

        const errors = mfaEnrollStep1Response?.data.errors;
        const isGovDeliveryError = errors && hasErrorMessage(errors, 'GOV_DELIVERY_ERROR');
        if (code === 400 && !isGovDeliveryError) {
            history.push("/login");
            return;
        }

        if (code === 201) {
            setShowInfoAlert(true);
            setIsResend(true);
        }
    }

    if (isCheckingSession || isCheckingCookie) {
        return <FullPageSpinner />;
    }

    if (!isVerified) {
        return <VerifyPassword type={type} flow={flow} onVerified={onSubmit} />;
    }

    const headingTitle = translate("enter_your_security_code");
    return (<>
        <BackNav />
        <Div isContainer paddingy={1} paddingyMD={4}>
            <form onSubmit={onSubmit} method="POST">
                <Div isRow>
                    <Div cols={12}>
                        <H1>
                            {confirmTitle}
                        </H1>
                        <Div isRow>
                            <Div cols={12} colsMD={8}>
                                {showInfoAlert && 
                                    <MfaSendSuccessAlert isResend={isResend} type={type} value={value} />}
                            </Div>
                        </Div>
                        <SmartErrorAlert
                            name={smartErrorName}
                            fieldRef={null}
                            apiResponse={isResendResponse ? step1EnrollResponse : step2EnrollResponse}
                            conversion={null}
                            invalidFields={null}
                            mfaIsAnotherCodeRequested={Boolean(isResendResponse)}
                            onInvalidField={() => {}}
                            parentHeading={headingTitle}
                            mfaFlow={flow}
                        />
                        <SmartErrorAlert
                            name='MfaDeleteMethod'
                            fieldRef={null}
                            apiResponse={deleteMethodResponse}
                            conversion={null}
                            invalidFields={null}
                            onInvalidField={() => {}}
                            parentHeading={headingTitle}
                            className={showInfoAlert ? "ds-u-margin-bottom--3" : ""}
                        />
                        <h2 className={ showInfoAlert ? "ds-u-margin-bottom--3 ds-text-heading--xl ds-u-margin-top--0" : "ds-u-margin-bottom--3 ds-text-heading--xl ds-u-margin-top--3"}>
                            {headingTitle}
                        </h2>
                        <TextField
                            autoComplete="one-time-code"
                            fieldClassName="ds-u-font-size--xl"
                            labelClassName="ds-u-margin-top--0"
                            className="ds-u-margin-bottom--3"
                            aria-invalid={!!(showFieldError && fieldErrorText)}
                            errorMessage={showFieldError ? fieldErrorText : null}
                            inputMode="numeric"
                            label={fieldLabel}
                            maxLength={6}
                            name={"securityCode"}
                            onChange={(event) => setSecurityCode(event.currentTarget.value)}
                            onBlur={() => setShowFieldError(true)}
                            pattern="[0-9]*"
                            size={"small"}
                            value={securityCode}
                        />
                    </Div>
                </Div>
                <GetAnotherCode
                    type={type}
                    onClick={getAnotherCode}
                    setShowInfoAlert={setShowInfoAlert}
                    mfaAnalyticsHeading={headingTitle}
                />
                <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                    text: translate('confirm'),
                    parentComponentHeading: headingTitle,
                }}>
                    {translate('confirm')}
                </SubmitButton>
            </form>
        </Div>
    </>);
}